import type {
  FormativeActivityDetail,
  NarrativeWritingHomeSlice,
  ReadingPracticeHomeSlice,
  ReadingTutorActivitySlice,
  Unit5TutorActivitySlice,
} from '../../../modules/tutor/api/malayFormativeProgress.client';
import {
  isMalayFormativeActivityKey,
  type MalayFormativeActivityKey,
} from '../../../modules/tutor/malayFormativeHomeCards';
import type { TeacherAnalyticsActivityOption } from './teacherAnalyticsActivityFilter';

/** Map teacher progress activity filter ids to Malay formative activity keys. */
export const MALAY_PROGRESS_ACTIVITY_EMPTY_KEY: Record<
  MalayFormativeActivityKey,
  'malayLearningEmptyReading' | 'malayLearningEmptyNarrative' | 'malayLearningEmptyVocabulary'
> = {
  malay_jendela_seni_reading: 'malayLearningEmptyReading',
  malay_narrative_learning_tutor: 'malayLearningEmptyNarrative',
  malay_unit_5: 'malayLearningEmptyVocabulary',
};

export const MALAY_PROGRESS_ACTIVITY_LABEL_KEY: Record<MalayFormativeActivityKey, string> = {
  malay_jendela_seni_reading: 'dashboard.malayFormative.activities.reading',
  malay_narrative_learning_tutor: 'dashboard.malayFormative.activities.narrative',
  malay_unit_5: 'dashboard.malayFormative.activities.unit5',
};

const NON_FORMATIVE_ACTIVITY_IDS = new Set(['communication_tutor', 'scenario_conversation']);

/** Resolve which formative activity keys to load for class-level progress (Unit 5 uses combined section). */
export function classLearningActivityKeysForFilter(
  activityFilter: string,
  activityOptions: TeacherAnalyticsActivityOption[],
): string[] {
  const keys = (() => {
    if (activityFilter.trim()) {
      const hit = activityOptions.find((o) => o.id === activityFilter.trim());
      if (!hit?.activityKey || NON_FORMATIVE_ACTIVITY_IDS.has(hit.id)) return [];
      return [hit.activityKey];
    }
    return activityOptions
      .filter((o) => o.activityKey && !NON_FORMATIVE_ACTIVITY_IDS.has(o.id))
      .map((o) => o.activityKey!);
  })();
  return keys.filter((key) => key !== 'malay_unit_5');
}

/** Whether the combined AI Tutor (Unit 5) section should render. */
export function shouldShowMalayUnit5CombinedSection(
  isMalay: boolean,
  activityFilter: string,
): boolean {
  if (!isMalay) return false;
  const filter = activityFilter.trim();
  // `vocabulary` kept as legacy alias for the merged AI Tutor filter.
  if (!filter || filter === 'ai_tutor' || filter === 'vocabulary') return true;
  return false;
}

export function isMalayAiTutorActivityFilter(activityFilter: string): boolean {
  const filter = activityFilter.trim();
  return filter === 'ai_tutor' || filter === 'vocabulary';
}

/** Whether class-level learning activity section should render for the current filter. */
export function shouldShowClassLearningProgressSection(
  activityFilter: string,
  activityOptions: TeacherAnalyticsActivityOption[],
): boolean {
  return classLearningActivityKeysForFilter(activityFilter, activityOptions).length > 0;
}

export function classLearningActivityOptionForKey(
  activityKey: string,
  activityOptions: TeacherAnalyticsActivityOption[],
): TeacherAnalyticsActivityOption | undefined {
  return activityOptions.find((o) => o.activityKey === activityKey);
}

export function classLearningActivityLabelKey(
  activityKey: string,
  activityOptions: TeacherAnalyticsActivityOption[],
): string {
  if (isMalayFormativeActivityKey(activityKey)) {
    return MALAY_PROGRESS_ACTIVITY_LABEL_KEY[activityKey];
  }
  const opt = classLearningActivityOptionForKey(activityKey, activityOptions);
  if (opt?.labelKey) {
    return `dashboard.teacher.analyticsScope.activities.${opt.labelKey}`;
  }
  return 'dashboard.teacher.analyticsScope.learningActivity';
}

export function classLearningActivityEmptyKey(
  activityKey: string,
  activityOptions: TeacherAnalyticsActivityOption[],
): string {
  if (isMalayFormativeActivityKey(activityKey)) {
    return `dashboard.teacher.analyticsScope.${MALAY_PROGRESS_ACTIVITY_EMPTY_KEY[activityKey]}`;
  }
  const opt = classLearningActivityOptionForKey(activityKey, activityOptions);
  switch (opt?.id) {
    case 'reading_comprehension':
      return 'dashboard.teacher.analyticsScope.malayLearningEmptyReading';
    case 'narrative_writing':
      return 'dashboard.teacher.analyticsScope.malayLearningEmptyNarrative';
    case 'vocabulary':
    case 'ai_tutor':
      return 'dashboard.teacher.analyticsScope.malayLearningEmptyAiTutor';
    default:
      return 'dashboard.teacher.analyticsScope.lessonProgressEmptyClass';
  }
}

/** Honest reading activity metrics (not mastery). */
export function formativeReadingActivitySummary(detail: FormativeActivityDetail | null | undefined): {
  hasActivity: boolean;
  passagesAttempted: number | null;
  passagesTotal: number | null;
  tutorSessions: number | null;
  learnerTurns: number | null;
  lastActivityAt: string | null;
  skillsWithEvidence: number;
} {
  const practice = formativeReadingPracticeEvidence(detail);
  const tutor = formativeReadingTutorActivityEvidence(detail);
  const skillsWithEvidence = formativeSkillsWithEvidence(detail);
  const lastPractice = practice?.last_practised_at ? String(practice.last_practised_at) : null;
  const lastTutor = tutor?.last_activity_at ? String(tutor.last_activity_at) : null;
  let lastActivityAt: string | null = null;
  if (lastPractice && lastTutor) {
    lastActivityAt = new Date(lastPractice) >= new Date(lastTutor) ? lastPractice : lastTutor;
  } else {
    lastActivityAt = lastPractice || lastTutor;
  }
  return {
    hasActivity: Boolean(practice || tutor),
    passagesAttempted: practice ? Number(practice.attempted_passages ?? 0) : null,
    passagesTotal: practice ? Number(practice.total_passages ?? 0) || null : null,
    tutorSessions: tutor ? Number(tutor.session_count ?? 0) : null,
    learnerTurns: tutor ? Number(tutor.learner_turn_count ?? 0) : null,
    lastActivityAt,
    skillsWithEvidence,
  };
}

/** Whether the Malay learning section should render for the current activity filter. */
export function shouldShowMalayLearningSection(
  activityFilter: string,
  activityOptions: TeacherAnalyticsActivityOption[],
): boolean {
  if (isMalayAiTutorActivityFilter(activityFilter)) return false;
  if (!activityFilter.trim()) return true;
  return formativeActivityKeysForProgressFilter(activityFilter, activityOptions).length > 0;
}

/** Resolve which Malay formative activities to show for the current activity filter. */
export function formativeActivityKeysForProgressFilter(
  activityFilter: string,
  activityOptions: TeacherAnalyticsActivityOption[],
): MalayFormativeActivityKey[] {
  if (activityFilter.trim()) {
    const hit = activityOptions.find((o) => o.id === activityFilter.trim());
    const key = hit?.activityKey;
    if (key && isMalayFormativeActivityKey(key) && key !== 'malay_unit_5') return [key];
    return [];
  }
  const keys: MalayFormativeActivityKey[] = [];
  for (const opt of activityOptions) {
    if (opt.activityKey && isMalayFormativeActivityKey(opt.activityKey) && opt.activityKey !== 'malay_unit_5') {
      keys.push(opt.activityKey);
    }
  }
  return keys;
}

export function formativeReadingPracticeEvidence(
  detail: FormativeActivityDetail | null | undefined,
): ReadingPracticeHomeSlice | null {
  const slice = detail?.reading_practice;
  if (!slice) return null;
  const attempted = Number(slice.attempted_passages ?? 0);
  return Number.isFinite(attempted) && attempted > 0 ? slice : null;
}

export function formativeReadingTutorActivityEvidence(
  detail: FormativeActivityDetail | null | undefined,
): ReadingTutorActivitySlice | null {
  const slice = detail?.reading_tutor_activity;
  if (!slice?.has_activity) return null;
  return slice;
}

export function formativeUnit5HasPracticeActivity(detail: FormativeActivityDetail | null | undefined): boolean {
  const slice = detail?.unit5_tutor_activity as Unit5TutorActivitySlice | null | undefined;
  return Boolean(slice?.has_practice_activity);
}

function narrativeWritingSliceHasEvidence(slice: NarrativeWritingHomeSlice | null | undefined): boolean {
  if (!slice) return false;
  const plot = String(slice.plot_planning_status ?? '').toLowerCase();
  if (plot && plot !== 'not_started') return true;
  const draft = String(slice.story_draft_status ?? '').toLowerCase();
  return Boolean(draft && draft !== 'not_started');
}

/** Participation / activity evidence (not skill mastery). */
export function formativeActivityHasParticipation(
  detail: FormativeActivityDetail | null | undefined,
): boolean {
  if (!detail?.ok) return false;
  if (formativeReadingPracticeEvidence(detail)) return true;
  if (formativeReadingTutorActivityEvidence(detail)) return true;
  if (narrativeWritingSliceHasEvidence(detail.narrative_writing)) return true;
  if (formativeUnit5HasPracticeActivity(detail)) return true;
  const progress = detail.progress as Record<string, unknown> | null | undefined;
  if (progress) {
    const completed = Number(progress.completed_units ?? 0);
    if (Number.isFinite(completed) && completed > 0) return true;
    const status = String(progress.status ?? '').toLowerCase();
    if (status && status !== 'not_started') return true;
  }
  return false;
}

export function formativeActivityHasEvidence(detail: FormativeActivityDetail | null | undefined): boolean {
  if (!detail?.ok) return false;
  if (formativeActivityHasParticipation(detail)) return true;
  if ((detail.observations?.length ?? 0) > 0) return true;
  const progress = detail.progress as Record<string, unknown> | null | undefined;
  if (progress) {
    const completed = Number(progress.completed_units ?? 0);
    if (Number.isFinite(completed) && completed > 0) return true;
    const status = String(progress.status ?? '').toLowerCase();
    if (status && status !== 'not_started') return true;
    const plot = String(progress.plot_planning_status ?? progress.narrative_plot_planning_status ?? '').toLowerCase();
    if (plot && plot !== 'not_started') return true;
    const draft = String(progress.story_draft_status ?? progress.narrative_story_draft_status ?? '').toLowerCase();
    if (draft && draft !== 'not_started') return true;
  }
  return (detail.skills ?? []).some(
    (s) =>
      s.current_level &&
      s.current_level !== 'not_enough_evidence' &&
      (s.valid_observation_count ?? 0) > 0,
  );
}

/** Whether a skill row counts toward teacher "Skills with evidence". */
export function formativeSkillCountsAsEvidence(skill: {
  current_level?: string | null;
  valid_observation_count?: number | null;
  counts_as_skill_evidence?: boolean;
}): boolean {
  if (!skill.current_level || skill.current_level === 'not_enough_evidence') return false;
  if ((skill.valid_observation_count ?? 0) <= 0) return false;
  // Backend omits the flag for non-narrative / older payloads → treat as true.
  if (skill.counts_as_skill_evidence === false) return false;
  return true;
}

export function formativeSkillsWithEvidence(detail: FormativeActivityDetail | null | undefined): number {
  return (detail?.skills ?? []).filter((s) => formativeSkillCountsAsEvidence(s)).length;
}

export function formativeProgressUnits(detail: FormativeActivityDetail | null | undefined): {
  completed: number | null;
  total: number | null;
} {
  // Prefer passage practice units for Reading when present (strongest activity signal).
  const reading = formativeReadingPracticeEvidence(detail);
  if (reading) {
    const completed = Number(reading.attempted_passages ?? 0);
    const total = Number(reading.total_passages ?? 0);
    return {
      completed: Number.isFinite(completed) ? completed : null,
      total: Number.isFinite(total) && total > 0 ? total : null,
    };
  }
  const tutor = formativeReadingTutorActivityEvidence(detail);
  if (tutor) {
    const completed = Number(tutor.learner_turn_count ?? tutor.session_count ?? 0);
    const total = Number(tutor.session_count ?? 0);
    return {
      completed: Number.isFinite(completed) && completed > 0 ? completed : null,
      total: Number.isFinite(total) && total > 0 ? total : null,
    };
  }
  const progress = detail?.progress as Record<string, unknown> | null | undefined;
  if (!progress) return { completed: null, total: null };
  const completedRaw = progress.completed_units;
  const totalRaw = progress.total_units;
  const completed =
    completedRaw != null && Number.isFinite(Number(completedRaw)) ? Number(completedRaw) : null;
  const total = totalRaw != null && Number.isFinite(Number(totalRaw)) ? Number(totalRaw) : null;
  return { completed, total };
}

/** Honest Narrative status when plot progress-unit counts are absent (skills ≠ progress steps). */
export function formativeNarrativeStatusSummary(
  detail: FormativeActivityDetail | null | undefined,
): { plotPlanningStatus: string | null; storyDraftStatus: string | null } {
  const slice = detail?.narrative_writing;
  const plot = String(slice?.plot_planning_status ?? '').trim().toLowerCase() || null;
  const draft = String(slice?.story_draft_status ?? '').trim().toLowerCase() || null;
  return { plotPlanningStatus: plot, storyDraftStatus: draft };
}

export function narrativeStatusLabelKey(status: string | null | undefined): string {
  switch (String(status || '').toLowerCase()) {
    case 'completed':
      return 'dashboard.teacher.analyticsScope.malayNarrativeStatusCompleted';
    case 'in_progress':
    case 'started':
      return 'dashboard.teacher.analyticsScope.malayNarrativeStatusInProgress';
    case 'not_started':
      return 'dashboard.teacher.analyticsScope.malayNarrativeStatusNotStarted';
    default:
      return 'dashboard.teacher.analyticsScope.malayNarrativeStatusUnknown';
  }
}

export type ClassFormativeStudentRow = {
  detail: FormativeActivityDetail | null;
};

export function summarizeClassFormativeActivity(rows: ClassFormativeStudentRow[]): {
  studentsInScope: number;
  studentsWithEvidence: number;
  totalSkillsWithEvidence: number;
} {
  let studentsWithEvidence = 0;
  let totalSkillsWithEvidence = 0;
  for (const row of rows) {
    if (formativeActivityHasParticipation(row.detail) || formativeSkillsWithEvidence(row.detail) > 0) {
      studentsWithEvidence += 1;
      totalSkillsWithEvidence += formativeSkillsWithEvidence(row.detail);
    }
  }
  return {
    studentsInScope: rows.length,
    studentsWithEvidence,
    totalSkillsWithEvidence,
  };
}

export type Unit5CombinedStudentRow = {
  detail: FormativeActivityDetail | null;
  tutorSessions: number;
};

export function summarizeUnit5CombinedClassActivity(rows: Unit5CombinedStudentRow[]): {
  studentsInScope: number;
  studentsWithActivity: number;
  totalVocabularySkillsWithEvidence: number;
} {
  let studentsWithActivity = 0;
  let totalVocabularySkillsWithEvidence = 0;
  for (const row of rows) {
    const hasActivity =
      row.tutorSessions > 0 ||
      formativeUnit5HasPracticeActivity(row.detail) ||
      formativeSkillsWithEvidence(row.detail) > 0;
    if (hasActivity) {
      studentsWithActivity += 1;
      totalVocabularySkillsWithEvidence += formativeSkillsWithEvidence(row.detail);
    }
  }
  return {
    studentsInScope: rows.length,
    studentsWithActivity,
    totalVocabularySkillsWithEvidence,
  };
}
